import { useUISlice } from "@/models/useUISlice";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "@/models/useAuth";
import { desktop, useAppStore } from "@/controller";
import PaywallBenefits from "@/components/onboarding/PaywallBenefits";
import { getPlatform, isNativeApp } from "@/platform";
import type { PurchasePackage } from "@/types";
import { gotoPrivacy, gotoTerms } from "@/product-links";
import { toast } from "react-hot-toast";
import { SubscriptionPlan } from "@/types";
import {
  CheckIcon,
  CloseIcon,
  CrownIcon,
  CursorIcon,
} from "@/components/Icons";
import { logClientEvent } from "@/telemetry";
import {
  BillingPeriod,
  getBillingPeriodFromIdentifier,
  getPackageBillingPeriod,
  getPackagePricePresentation,
  getYearlySavingsPct,
} from "@/models/paywallMath";
import { estimateHoursSavedPerMonth } from "@/models/onboardingPersona";
import type { Persona } from "@/models/onboardingPersona";
import { actions } from "@/controller";
import { i18n } from "@/locales";
import { getStoreProofTranslationValues } from "@/product-proof";

type Translate = (key: string, options?: Record<string, unknown>) => string;
type WebSubscriptionPlans = Record<string, SubscriptionPlan | undefined>;

export const restoreNativePurchase = async () => {
  const result = await actions.subscription.revenueCatRestorePurchase();
  if ("error" in result) {
    toast.error(i18n.t("actions.noSubscriptionText"));
    return;
  }
  toast.success(i18n.t("actions.restorePurchaseSuccess"));
};

const billingPeriodSortOrder: Record<BillingPeriod, number> = {
  annual: 0,
  monthly: 1,
  weekly: 2,
};

const getOrderedPackages = (
  packages?: PurchasePackage[],
): PurchasePackage[] | undefined => {
  if (!packages) return undefined;

  return [...packages]
    .filter((pkg): pkg is PurchasePackage =>
      Boolean(pkg?.identifier && pkg?.product),
    )
    .sort((firstPackage, secondPackage) => {
      const firstPeriod = getPackageBillingPeriod(firstPackage);
      const secondPeriod = getPackageBillingPeriod(secondPackage);

      return (
        (firstPeriod ? billingPeriodSortOrder[firstPeriod] : 99) -
        (secondPeriod ? billingPeriodSortOrder[secondPeriod] : 99)
      );
    });
};

const getPreferredPackage = (
  packages?: PurchasePackage[],
): PurchasePackage | undefined =>
  packages?.find((pkg) => getPackageBillingPeriod(pkg) === "annual") ||
  packages?.[0];

const getPackageAccessLabel = (t: Translate, pkg: PurchasePackage) => {
  const billingPeriod = getPackageBillingPeriod(pkg);

  if (billingPeriod === "annual") {
    return t("alertWidgetPaywall.yearlyAccess");
  }
  if (billingPeriod === "weekly") {
    return t("alertWidgetPaywall.weeklyAccess");
  }

  return t("alertWidgetPaywall.monthlyAccess");
};

const getBillingPeriodLabel = (t: Translate, billingPeriod?: BillingPeriod) => {
  if (billingPeriod === "annual") {
    return t("alertWidgetPaywall.perYear");
  }
  if (billingPeriod === "weekly") {
    return t("alertWidgetPaywall.perWeek");
  }

  return t("alertWidgetPaywall.perMonth");
};

const getPackagePeriodLabel = (t: Translate, pkg?: PurchasePackage) =>
  getBillingPeriodLabel(t, getPackageBillingPeriod(pkg));

const getDefaultPriceString = (identifier: string) => {
  const billingPeriod = getBillingPeriodFromIdentifier(identifier);

  if (billingPeriod === "annual") return "US$49.99";
  if (billingPeriod === "weekly") return "US$4.99";

  return "US$19.99";
};

const playAnnualNudgeHaptic = () => {
  if (getPlatform() === "android") navigator.vibrate?.(10);
};

const AlertWidgetPaywall = () => {
  const visible = useUISlice((state) => state.paywallVisible);
  const paywallParams: {
    callback?: () => void;
    title?: string;
    subtitle?: string;
    utmSource?: string;
  } = typeof visible === "object" ? visible : {};
  const setVisible = useUISlice((state) => state.setPaywallVisible);
  const { t, i18n: activeI18n } = useTranslation();
  const storeProof = getStoreProofTranslationValues(
    activeI18n.resolvedLanguage || activeI18n.language,
  );
  const revenueCatPackages = useAppStore(
    (state) => state.nativePurchase?.packages,
  );
  const country = useAppStore((state) => state.country);
  const webSubscriptionPlans = useAppStore(
    (state) => state.webSubscriptionPlans,
  );
  // Persona — used to pick the persona-flavored features list instead of the
  // generic one. Null when the user hasn't completed onboarding yet.
  const onboardingPersona = useAppStore((state) => state.onboardingPersona);
  const onboardingIntent = useAppStore((state) => state.onboardingIntent);
  const isSubscribed = useAppStore((state) => state.isSubscribed);
  const isDesktop = desktop.isAvailable;
  const isDesktopOnboarding =
    isDesktop && paywallParams.utmSource === "desktop-onboarding";
  const dialogRef = useRef<HTMLDialogElement>(null);
  const packages = useMemo(() => {
    if (getPlatform() === "web") {
      return AlertWidgetPaywall.getWebPackages(
        country,
        webSubscriptionPlans as WebSubscriptionPlans,
      );
    }

    return revenueCatPackages;
  }, [revenueCatPackages, country, webSubscriptionPlans]);

  const orderedPackages = useMemo(() => {
    const ordered = getOrderedPackages(packages);
    if (!ordered) return ordered;
    // Native paywall only ships yearly + weekly — no monthly tier.
    // (Web keeps whatever remote config returns.)
    if (getPlatform() !== "web") {
      return ordered.filter((pkg) => {
        const period = getPackageBillingPeriod(pkg);
        return period === "annual" || period === "weekly";
      });
    }
    return ordered;
  }, [packages]);
  const preferredPackage = useMemo(
    () => getPreferredPackage(orderedPackages),
    [orderedPackages],
  );
  const isNative = isNativeApp();

  const [currentPackage, setCurrentPackage] = useState<
    PurchasePackage | undefined
  >(preferredPackage);
  const [showAnnualNudge, setShowAnnualNudge] = useState(false);
  const [purchaseFailed, setPurchaseFailed] = useState(false);
  const paywallCallback = paywallParams?.callback;

  const _onDismiss = useCallback(() => {
    setVisible(false);
    paywallCallback?.();
  }, [paywallCallback, setVisible]);

  const yearlyPackage = useMemo(
    () =>
      orderedPackages?.find((pkg) => getPackageBillingPeriod(pkg) === "annual"),
    [orderedPackages],
  );
  const comparisonPackage = useMemo(
    () =>
      orderedPackages?.find((pkg) => getPackageBillingPeriod(pkg) !== "annual"),
    [orderedPackages],
  );
  const yearlySavingsPct = useMemo(
    () => getYearlySavingsPct(yearlyPackage, comparisonPackage),
    [yearlyPackage, comparisonPackage],
  );

  const _handleDismiss = useCallback(() => {
    logClientEvent("paywall.skipped", {
      utmSource: paywallParams?.utmSource,
      surface: "alert-widget-paywall",
    });
    _onDismiss();
  }, [paywallParams?.utmSource, _onDismiss]);

  useEffect(() => {
    if (!visible || !isDesktop) return;
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, [visible, isDesktop]);

  useEffect(() => {
    // A returning subscriber's profile can arrive after setup completes.
    if (visible && isDesktopOnboarding && isSubscribed) _onDismiss();
  }, [visible, isDesktopOnboarding, isSubscribed, _onDismiss]);

  useEffect(() => {
    if (visible) setCurrentPackage(preferredPackage);
    setShowAnnualNudge(false);
    setPurchaseFailed(false);
  }, [visible, preferredPackage]);

  useEffect(() => {
    if (!visible) return;

    if (orderedPackages?.length) {
      setCurrentPackage((currentPackage) => {
        const isCurrentPackageAvailable = orderedPackages.some(
          (pkg) => pkg.identifier === currentPackage?.identifier,
        );

        return isCurrentPackageAvailable ? currentPackage : preferredPackage;
      });

      return;
    }

    if (isNative && !packages) {
      void actions.subscription.configureRevenueCat().then((result) => {
        if ("error" in result) _onDismiss();
      });
    }
  }, [
    packages,
    visible,
    preferredPackage,
    orderedPackages,
    isNative,
    _onDismiss,
  ]);

  // [55] paywall viewed — fires once per mount where `visible` is truthy.
  useEffect(() => {
    if (!visible) return;
    logClientEvent("paywall.viewed", {
      utmSource: paywallParams?.utmSource,
      platform: getPlatform(),
      hasPackagesAtMount: !!orderedPackages?.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // [55.a] packages loaded — fires when the package list transitions from
  // empty to populated for this paywall view.
  useEffect(() => {
    if (!visible || !orderedPackages?.length) return;
    logClientEvent("paywall.packagesLoaded", {
      utmSource: paywallParams?.utmSource,
      packageCount: orderedPackages.length,
      identifiers: orderedPackages.map((p) => p.identifier),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, orderedPackages?.length]);

  const _gotoTerms = () => {
    setVisible(false);
    gotoTerms();
  };

  const _gotoPrivacy = () => {
    setVisible(false);
    gotoPrivacy();
  };

  if (!visible) return null;

  if (isNativeApp() && !currentPackage?.product) return <div />;

  const content = (
    <div className="glass-thick fixed inset-0 z-[1000] flex flex-col rounded-none overflow-hidden">
      <style>{`
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-gentle-bounce {
          animation: gentle-bounce 2s infinite ease-in-out;
        }
        @keyframes click-animation {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(0.9) rotate(-15deg); }
          50% { transform: scale(1) rotate(0deg); }
        }
        .animate-click {
          animation: click-animation 1.5s infinite ease-in-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-gentle-bounce,
          .animate-click {
            animation: none;
          }
        }
      `}</style>
      <button
        data-action-id="paywall.dismiss"
        className="icon-btn absolute z-[100] cursor-pointer active:scale-95 transition-transform duration-200"
        style={{
          top: isNative ? 48 : 24,
          left: 24,
        }}
        onClick={_handleDismiss}
        aria-label={t("modalContainer.closeModal")}
      >
        <CloseIcon
          size={22}
          className="text-[var(--text-primary)] opacity-80"
        />
      </button>

      <div className="flex min-h-0 flex-col h-full w-full max-w-lg mx-auto relative">
        <div className="flex-1 min-h-0 w-full mt-12 overflow-y-auto overflow-x-hidden pb-4 no-scrollbar px-6">
          {isDesktop ? (
            <PaywallBenefits
              intent={onboardingIntent}
              persona={onboardingPersona}
              setupReady={isDesktopOnboarding}
            />
          ) : (
            <>
              <div className="flex flex-col items-center mb-6">
                <div
                  aria-hidden="true"
                  className="w-14 h-14 mb-4 rounded-2xl bg-gradient-to-tr from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] flex items-center justify-center shadow-lg shadow-[var(--premium-cta-glow)]"
                >
                  <CrownIcon size={24} className="text-white" />
                </div>
                <h2 className="text-balance text-2xl font-black text-[var(--text-primary)] text-center tracking-tight leading-tight">
                  {t("alertWidgetPaywall.title1")}
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent)] to-[var(--text-primary)]">
                    {t("alertWidgetPaywall.title2")}
                  </span>{" "}
                  {t("alertWidgetPaywall.title3")}
                </h2>
              </div>

              <div className="mb-4 flex flex-col gap-1">
                {(() => {
                  // Persona-flavored features when persona is set; fall back to
                  // the generic alertWidgetPaywall.features for users who reached
                  // this paywall without going through onboarding (or set Other).
                  let features: string[] | null = null;
                  if (onboardingPersona) {
                    const persona = t(
                      `onboarding.paywall.features_${onboardingPersona}`,
                      { returnObjects: true },
                    ) as string[] | string;
                    if (Array.isArray(persona)) features = persona;
                  }
                  if (!features) {
                    const generic = t("alertWidgetPaywall.features", {
                      returnObjects: true,
                    }) as string[] | string;
                    features = Array.isArray(generic) ? generic : [];
                  }
                  // i18next skips interpolation for `returnObjects` arrays — patch
                  // the `{{hours}}` placeholders manually so the persona's
                  // hours-saved bullet matches what the user saw on the result step.
                  const hours = estimateHoursSavedPerMonth(
                    (onboardingPersona as Persona | null) ?? null,
                  );
                  features = features.map((line) =>
                    line.replace(/\{\{hours\}\}/g, String(hours)),
                  );
                  return features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-center p-0"
                    >
                      <span className="text-[15px] font-medium text-[var(--text-primary)]/90 text-center">
                        {feature}
                      </span>
                    </div>
                  ));
                })()}
              </div>

              {/* Social Proof Section - Minimalist Glass Card */}
              <div className="flex flex-col items-center gap-4 py-2 mb-6">
                <div className="glass flex items-center gap-1.5 px-3.5 py-1.5 rounded-full">
                  <span aria-hidden="true" className="text-xs">
                    ⭐⭐⭐⭐⭐
                  </span>
                  <span className="text-[11px] text-[var(--text-primary)] font-bold tracking-wide uppercase">
                    {t("alertWidgetPaywall.rating", storeProof)}
                  </span>
                </div>

                <div className="glass p-5 rounded-3xl w-full">
                  <p className="text-[14px] font-medium text-[var(--text-primary)] text-center leading-relaxed">
                    “{t("alertWidgetPaywall.testimonialQuote")}”
                  </p>
                  <p className="text-[10px] text-[var(--text-secondary)] font-bold text-center mt-2 uppercase tracking-wider opacity-60">
                    {t("alertWidgetPaywall.testimonialAuthor")}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Fixed Bottom Section - Plans & CTA */}
        <div
          className={`shrink-0 px-6 relative z-10 ${isDesktop ? "pt-3 pb-5" : "pt-6"}`}
          style={{
            // Keeps Terms/Privacy/Restore above the phone's navigation bar.
            ...(isDesktop
              ? {}
              : {
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 2rem)",
                }),
            background:
              "linear-gradient(to top, var(--bg-secondary) 80%, transparent)",
          }}
        >
          <div
            className={`flex flex-col ${isDesktop ? "gap-2 mb-3" : "gap-3 mb-6"}`}
          >
            {orderedPackages?.map((pkg) => {
              const billingPeriod = getPackageBillingPeriod(pkg);
              const isAnnual = billingPeriod === "annual";
              const pricePresentation = getPackagePricePresentation(pkg);
              const isSelected = pkg.identifier === currentPackage?.identifier;

              return (
                <button
                  data-action-id="paywall.package.select"
                  aria-pressed={isSelected}
                  onClick={() => {
                    const isNewWeeklySelection =
                      billingPeriod === "weekly" && !isSelected;
                    setCurrentPackage(pkg);
                    setShowAnnualNudge(
                      billingPeriod === "weekly" ||
                        (isDesktop && billingPeriod === "monthly"),
                    );
                    if (isNewWeeklySelection) playAnnualNudgeHaptic();
                    logClientEvent("paywall.planSelected", {
                      utmSource: paywallParams?.utmSource,
                      identifier: pkg.identifier,
                      billingPeriod,
                    });
                  }}
                  key={pkg.identifier}
                  className={`group relative flex flex-row items-center justify-between ${isDesktop ? "min-h-16 px-4 py-3 rounded-2xl" : "px-5 py-5 rounded-3xl"} cursor-pointer transition-[transform,background-color,box-shadow] duration-300 w-full
                    ${
                      isSelected
                        ? "selected-surface scale-[1.02]"
                        : "bg-[var(--fill-1)] hover:bg-[var(--fill-2)] active:scale-[0.98]"
                    }`}
                >
                  {/* Best Offer Badge — computed % when prices are numeric */}
                  {isAnnual && (
                    <div
                      className={`absolute -top-2.5 right-6 px-2.5 py-0.5 rounded-full z-[1] flex items-center gap-1 shadow-sm transition-colors duration-300 bg-[var(--accent)] ${
                        showAnnualNudge
                          ? "animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
                          : ""
                      }`}
                    >
                      {showAnnualNudge && (
                        <span
                          aria-hidden="true"
                          className="text-sm leading-none"
                        >
                          🐱
                        </span>
                      )}
                      <span
                        className={`text-[9px] font-extrabold uppercase tracking-wider text-[var(--bg-primary)]`}
                      >
                        {showAnnualNudge
                          ? t("alertWidgetPaywall.annualPriceIncreasingSoon", {
                              defaultValue:
                                "Current annual price increases soon",
                            })
                          : yearlySavingsPct
                            ? t("alertWidgetPaywall.savePct", {
                                pct: yearlySavingsPct,
                              })
                            : t("alertWidgetPaywall.bestOffer")}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col gap-0.5 items-start">
                    <span
                      className={`text-[15px] font-bold text-start transition-colors duration-300 text-[var(--text-primary)]`}
                    >
                      {getPackageAccessLabel(t, pkg)}
                    </span>

                    <span className="text-[12px] font-medium text-start text-[var(--text-secondary)]">
                      {isAnnual
                        ? t("alertWidgetPaywall.annualPricing", {
                            price: pkg.product.priceString,
                          })
                        : t("alertWidgetPaywall.premiumPlan")}
                    </span>
                  </div>

                  <div className="flex flex-col items-end">
                    <span
                      className={`text-[16px] font-bold transition-colors duration-300 text-[var(--text-primary)]`}
                    >
                      {pricePresentation.priceString}
                    </span>
                    <span
                      className={`text-[11px] transition-colors duration-300
                      ${isSelected ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}
                    >
                      /
                      {getBillingPeriodLabel(
                        t,
                        pricePresentation.billingPeriod,
                      ).toLowerCase()}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Decision-moment trust row — pulled inline with the CTA so users
              see the social-proof anchor right when they decide. The
              cancel-anytime message lives once in the footer guarantee
              below, no need for it here as well. */}
          {!isDesktop && (
            <div className="flex flex-row items-center justify-center gap-3 mb-3 opacity-90">
              <span className="text-[11px] font-bold text-[var(--text-primary)] tracking-wide">
                {t("onboarding.paywall.trustLine")}
              </span>
              <span className="text-[var(--text-tertiary)]">·</span>
              <span className="text-[11px] font-bold text-[var(--text-primary)] tracking-wide">
                {t("alertWidgetPaywall.rating", storeProof)}
              </span>
            </div>
          )}

          <div className={`${isDesktop ? "mb-1.5" : "mb-4"} px-1`}>
            <button
              data-action-id="paywall.purchase"
              disabled={!currentPackage}
              className={`w-full rounded-full bg-gradient-to-r from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] text-[var(--premium-cta-text)] font-bold tracking-wide flex items-center justify-center active:scale-[0.98] transition-transform shadow-lg shadow-[var(--premium-cta-glow)] relative disabled:opacity-50 ${isDesktop ? "h-14 text-[18px]" : "h-16 text-[19px] animate-gentle-bounce"}`}
              onClick={() => {
                setPurchaseFailed(false);
                logClientEvent("paywall.purchaseStarted", {
                  package: currentPackage?.identifier,
                  utmSource: paywallParams?.utmSource,
                });

                if (!currentPackage) return;

                AlertWidgetPaywall.handlePurchase({
                  package: currentPackage,
                  source: paywallParams?.utmSource || "alert-widget-paywall",
                  onError: isDesktop
                    ? () => setPurchaseFailed(true)
                    : undefined,
                  callback: () => {
                    _onDismiss();
                    logClientEvent("paywall.purchaseCompleted", {
                      package: currentPackage?.identifier,
                      utmSource: paywallParams?.utmSource,
                    });
                    toast.success(t("alertWidgetPaywall.subscriptionSuccess"));
                  },
                });
              }}
            >
              <span>{t("alertWidgetPaywall.buttonContinue")}</span>
              {!isDesktop && (
                <div
                  aria-hidden="true"
                  className="absolute right-6 top-1/2 -translate-y-1/2 animate-click"
                >
                  <CursorIcon
                    size={28}
                    className="text-[var(--premium-cta-text)] opacity-80"
                  />
                </div>
              )}
            </button>
            {purchaseFailed && (
              <p
                role="alert"
                className="mt-2 text-center text-sm text-[var(--destructive)]"
              >
                {t("actions.subscriptionError")}
              </p>
            )}
            <p className="mt-2 text-center text-[11px] leading-4 text-[var(--text-secondary)]">
              {isNative
                ? t("alertWidgetPaywall.billingDisclosure", {
                    price: currentPackage?.product.priceString || "",
                  })
                : t("alertWidgetPaywall.checkoutDisclosure")}
            </p>
          </div>

          {/* Minimal Footer */}
          <div>
            {isDesktop ? (
              <button
                data-action-id="paywall.dismiss"
                onClick={_handleDismiss}
                className="min-h-11 w-full text-center text-[14px] font-semibold text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
              >
                {t("onboarding.paywall.skip")}
              </button>
            ) : (
              <div className="flex flex-row items-center justify-center gap-1.5 mb-4 opacity-80">
                <div className="p-0.5 rounded-full bg-[var(--success)]/20">
                  <CheckIcon size={10} className="text-[var(--success)]" />
                </div>
                <span className="text-[11px] font-medium text-[var(--text-secondary)]">
                  {t("alertWidgetPaywall.guaranteeTitle")}
                </span>
              </div>
            )}

            <div className="flex flex-row justify-center gap-6">
              <button
                data-action-id="legal.terms.open"
                onClick={_gotoTerms}
                className="text-[11px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
              >
                {t("alertWidgetPaywall.terms")}
              </button>
              <button
                data-action-id="legal.privacy.open"
                onClick={_gotoPrivacy}
                className="text-[11px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
              >
                {t("alertWidgetPaywall.privacy")}
              </button>
              {isNative && (
                <button
                  data-action-id="subscription.restore"
                  onClick={restoreNativePurchase}
                  className="text-[11px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  {t("alertWidgetPaywall.restore")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return isDesktop ? (
    <dialog
      ref={dialogRef}
      data-testid="desktop-paywall"
      aria-label={t("alertWidgetPaywall.premiumPlan")}
      onCancel={(event) => {
        event.preventDefault();
        _handleDismiss();
      }}
      className="m-0 h-dvh max-h-none w-screen max-w-none border-0 bg-transparent p-0 text-[var(--text-primary)]"
    >
      {content}
    </dialog>
  ) : (
    content
  );
};

AlertWidgetPaywall.handlePurchase = async (params: {
  package: PurchasePackage;
  source?: string;
  callback?: () => void;
  onError?: () => void;
}) => {
  const reportError = () => {
    if (params.onError) params.onError();
    else toast.error(i18n.t("actions.subscriptionError"));
  };
  if (isNativeApp()) {
    useAuth.signInAsGuest({
      onError: () => {
        logClientEvent("paywall.purchaseFinished", {
          package: params.package.identifier,
          status: "guestAuthFailed",
          source: params.source,
        });
        reportError();
      },
      callback: async () => {
        const result = await actions.subscription.revenueCatPurchase(
          params.package.identifier,
        );
        if ("error" in result) {
          logClientEvent("paywall.purchaseFinished", {
            package: params.package.identifier,
            status:
              result.error === "purchaseCancelled" ? "cancelled" : "failed",
            reason: result.error,
            source: params.source,
          });
          if (result.error !== "purchaseCancelled") {
            reportError();
          }
          return;
        }
        logClientEvent("paywall.purchaseFinished", {
          package: params.package.identifier,
          status: "completed",
          source: params.source,
        });
        const transcriptId = new URLSearchParams(window.location.search).get(
          "id",
        );
        const transcript = useAppStore
          .getState()
          .transcripts.find((item) => item.id === transcriptId);
        if (
          transcriptId &&
          transcript &&
          ["voice_recording", "audio_or_video_import"].includes(
            transcript.type || "",
          )
        ) {
          void actions.transcript.transcribeFullAudioAfterPurchase(
            transcriptId,
          );
        }
        params.callback?.();
        useAuth.protectGuestPurchase({
          callback: () => {
            void actions.user.profile();
          },
        });
      },
    });
  } else {
    // Browser checkout belongs to the durable web device identity. Any later
    // sign-in upgrades that identity; native remains on RevenueCat.
    void useAuth.signInAsGuest({
      onError: reportError,
      callback: async () => {
        const result = await actions.subscription.stripeCheckout({
          subscriptionPlan: params.package.identifier as SubscriptionPlan,
        });
        if ("error" in result) {
          reportError();
          return;
        }
        window.open(result.value, "_self");
      },
    });
  }
};

const AlertWidgetPaywallFooter = ({
  source,
  completeTranscript = false,
  fileName,
  remainingMinutes,
}: {
  source?: "guestTranscript" | "uploadEntry";
  completeTranscript?: boolean;
  /** The file this offer unlocks, named in the subtitle when known. */
  fileName?: string;
  remainingMinutes?: number | null;
}) => {
  const { t } = useTranslation();
  const setPaywallVisible = useUISlice((state) => state.setPaywallVisible);
  const isSubscribed = useAppStore((state) => state.isSubscribed);
  const revenueCatPackages = useAppStore(
    (state) => state.nativePurchase?.packages,
  );
  const country = useAppStore((state) => state.country);
  const webSubscriptionPlans = useAppStore(
    (state) => state.webSubscriptionPlans,
  );
  const packages = useMemo(() => {
    if (getPlatform() === "web") {
      return AlertWidgetPaywall.getWebPackages(
        country,
        webSubscriptionPlans as WebSubscriptionPlans,
      );
    }

    return revenueCatPackages;
  }, [revenueCatPackages, country, webSubscriptionPlans]);
  const orderedPackages = useMemo(() => {
    const ordered = getOrderedPackages(packages);
    if (!ordered) return ordered;
    if (getPlatform() !== "web") {
      return ordered.filter((pkg) => {
        const period = getPackageBillingPeriod(pkg);
        return period === "annual" || period === "weekly";
      });
    }
    return ordered;
  }, [packages]);
  const displayedPackages = useMemo(() => {
    if (!orderedPackages?.length) return orderedPackages;
    // Monthly is the smallest step for someone holding one file. The full
    // paywall still preselects annual and Stripe offers the annual upgrade at
    // checkout; native sells no monthly tier, so it keeps annual.
    const monthly = orderedPackages.find(
      (pkg) => getPackageBillingPeriod(pkg) === "monthly",
    );
    return [monthly || orderedPackages[0]];
  }, [orderedPackages]);
  const canSeeAllPlans =
    source !== "guestTranscript" && (orderedPackages?.length || 0) > 1;
  const translatedFeatures = t("alertWidgetPaywall.features", {
    returnObjects: true,
  }) as string[] | string;
  const subscriptionFeatures = Array.isArray(translatedFeatures)
    ? translatedFeatures
    : [];

  if (isSubscribed || !displayedPackages?.length) return null;

  return (
    <div className="p-6 pb-12 bg-gradient-to-br from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] shadow-2xl">
      <p className="text-[28px] text-[var(--premium-cta-text)] font-black mb-2 tracking-tight">
        {t(
          completeTranscript
            ? "alertWidgetPaywall.accuracyTitle"
            : "alertWidgetPaywall.footerTitle",
        )}
      </p>
      <p className="text-[15px] leading-6 text-[var(--premium-cta-text)]/80 font-medium break-words">
        {completeTranscript
          ? t("alertWidgetPaywall.accuracySubtitle")
          : fileName && remainingMinutes
            ? t("alertWidgetPaywall.footerSubtitleRemaining", {
                fileName,
                minutes: remainingMinutes,
              })
            : fileName
              ? t("alertWidgetPaywall.footerSubtitleFile", { fileName })
              : t("alertWidgetPaywall.footerSubtitle")}
        {/* Download is what an entry visitor just found locked. */}
        {source === "uploadEntry" &&
          ` ${t("alertWidgetPaywall.entryDownloadSubtitle")}`}
      </p>
      {subscriptionFeatures.length > 0 && (
        <div
          data-testid="subscription-benefits"
          className="mt-5 py-2 text-[var(--premium-cta-text)]"
        >
          <p className="mb-3 text-[13px] font-black uppercase tracking-[0.12em]">
            {t("onboarding.featureShowcase.subhead")}
          </p>
          <ul className="flex flex-col gap-2.5">
            {subscriptionFeatures.map((feature) => (
              <li
                key={feature}
                className="text-[13px] font-bold leading-5 tracking-wide"
              >
                {feature}
              </li>
            ))}
          </ul>
        </div>
      )}

      {displayedPackages.map((pkg, index) => {
        const isPrimaryPlan = index === 0;

        return (
          <button
            key={`${pkg.identifier}-${index}`}
            data-action-id="paywall.purchase"
            onClick={() => {
              if (source === "guestTranscript") {
                logClientEvent("guest.checkoutSelected", {
                  source: "transcriptFooter",
                  billingPeriod: getPackageBillingPeriod(pkg),
                });
              }
              logClientEvent("paywall.purchaseStarted", {
                package: pkg.identifier,
              });
              AlertWidgetPaywall.handlePurchase({
                package: pkg,
                source: source || "partial-transcript",
                callback: () => {
                  logClientEvent("paywall.purchaseCompleted", {
                    package: pkg.identifier,
                    utmSource: "paywall --> footer",
                  });
                },
              });
            }}
            className={`w-full min-h-[var(--control-lg)] py-3 rounded-2xl flex items-center justify-center px-4 transition-transform active:scale-[0.98] ${
              isPrimaryPlan
                ? "mt-6 bg-[var(--premium-cta-text)] text-white shadow-lg shadow-black/20 font-bold text-lg"
                : "mt-3 bg-transparent text-[var(--premium-cta-text)]/70 font-medium underline decoration-[var(--premium-cta-text)]/25 underline-offset-4 text-sm"
            }`}
          >
            <span
              className={`${
                isPrimaryPlan ? "text-lg tracking-wide" : "text-sm"
              }`}
            >
              {source !== "guestTranscript" &&
              getPackageBillingPeriod(pkg) === "annual"
                ? t("alertWidgetPaywall.footerAnnualCta", {
                    price: pkg.product.priceString,
                    defaultValue: "Unlock with annual — {{price}}/year",
                  })
                : t("alertWidgetPaywall.footerPrice", {
                    price: pkg.product.priceString,
                    period: getPackagePeriodLabel(t, pkg),
                  })}
            </span>
          </button>
        );
      })}

      {canSeeAllPlans && (
        <button
          data-action-id="paywall.package.select"
          onClick={() =>
            setPaywallVisible({
              utmSource: "partial-transcript",
            })
          }
          className="mt-3 w-full min-h-[var(--control-lg)] rounded-2xl px-4 text-sm font-medium text-[var(--premium-cta-text)]/80 underline decoration-[var(--premium-cta-text)]/30 underline-offset-4 transition-colors hover:text-[var(--premium-cta-text)] active:scale-[0.98]"
        >
          {t("alertWidgetPaywall.seeAllPlans", {
            defaultValue: "See all plans",
          })}
        </button>
      )}

      {source === "guestTranscript" && (
        <p className="mt-4 text-center text-xs font-medium leading-5 text-[var(--premium-cta-text)]/75">
          {t("alertWidgetPaywall.checkoutDisclosure", {
            defaultValue:
              "Opens secure checkout. Review monthly or yearly pricing before paying.",
          })}
        </p>
      )}

      <div className="flex flex-row justify-center gap-6 mt-4">
        <button
          data-action-id="legal.terms.open"
          onClick={gotoTerms}
          className="text-xs text-[var(--premium-cta-text)]/70 hover:text-[var(--premium-cta-text)] transition-colors"
        >
          {t("alertWidgetPaywall.terms")}
        </button>
        <button
          data-action-id="legal.privacy.open"
          onClick={gotoPrivacy}
          className="text-xs text-[var(--premium-cta-text)]/70 hover:text-[var(--premium-cta-text)] transition-colors"
        >
          {t("alertWidgetPaywall.privacy")}
        </button>

        {isNativeApp() && (
          <button
            data-action-id="subscription.restore"
            onClick={restoreNativePurchase}
            className="text-xs text-[var(--premium-cta-text)]/70 hover:text-[var(--premium-cta-text)] transition-colors"
          >
            {t("alertWidgetPaywall.restore")}
          </button>
        )}
      </div>
    </div>
  );
};

AlertWidgetPaywall.Footer = AlertWidgetPaywallFooter;
AlertWidgetPaywall.getPackageBillingPeriod = getPackageBillingPeriod;
AlertWidgetPaywall.getPackagePeriodLabel = getPackagePeriodLabel;

AlertWidgetPaywall.priceMapper = {
  US: {
    yearly_price_per_week: "US$0.96",
    low_tier_weekly: "US$4.99",
    low_tier_monthly: "US$14.99",
    low_tier_annual: "US$39.99",
    mid_tier_weekly: "US$7.99",
    mid_tier_monthly: "US$19.99",
    mid_tier_annual: "US$49.99",
    top_tier_weekly: "US$9.99",
    top_tier_monthly: "US$19.99",
    top_tier_annual: "US$59.99",
  },
  CA: {
    yearly_price_per_week: "US$0.96 ≈ CA$1.33",
    low_tier_weekly: "US$4.99 ≈ CA$7.19",
    low_tier_monthly: "US$14.99 ≈ CA$21.59",
    low_tier_annual: "US$39.99 ≈ CA$57.59",
    mid_tier_weekly: "US$7.99 ≈ CA$11.19",
    mid_tier_monthly: "US$19.99 ≈ CA$28.99",
    mid_tier_annual: "US$49.99 ≈ CA$71.99",
    top_tier_weekly: "US$9.99 ≈ CA$13.99",
    top_tier_monthly: "US$19.99 ≈ CA$28.99",
    top_tier_annual: "US$59.99 ≈ CA$86.39",
  },
  GB: {
    yearly_price_per_week: "US$0.96 ≈ £0.79",
    low_tier_weekly: "US$4.99 ≈ £4.09",
    low_tier_monthly: "US$14.99 ≈ £12.29",
    low_tier_annual: "US$39.99 ≈ £32.79",
    mid_tier_weekly: "US$7.99 ≈ £6.55",
    mid_tier_monthly: "US$19.99 ≈ £16.39",
    mid_tier_annual: "US$49.99 ≈ £40.99",
    top_tier_weekly: "US$9.99 ≈ £8.19",
    top_tier_monthly: "US$19.99 ≈ £16.39",
    top_tier_annual: "US$59.99 ≈ £49.19",
  },
  AU: {
    yearly_price_per_week: "US$0.96 ≈ AU$1.54",
    low_tier_weekly: "US$4.99 ≈ AU$7.98",
    low_tier_monthly: "US$14.99 ≈ AU$23.98",
    low_tier_annual: "US$39.99 ≈ AU$63.98",
    mid_tier_weekly: "US$7.99 ≈ AU$12.78",
    mid_tier_monthly: "US$19.99 ≈ AU$31.98",
    mid_tier_annual: "US$49.99 ≈ AU$79.98",
    top_tier_weekly: "US$9.99 ≈ AU$15.98",
    top_tier_monthly: "US$19.99 ≈ AU$31.98",
    top_tier_annual: "US$59.99 ≈ AU$95.98",
  },
  DE: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  FR: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  IT: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  ES: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  NL: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  BE: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  AT: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  CH: {
    yearly_price_per_week: "US$0.96 ≈ CHF0.87",
    low_tier_weekly: "US$4.99 ≈ CHF4.54",
    low_tier_monthly: "US$14.99 ≈ CHF13.64",
    low_tier_annual: "US$39.99 ≈ CHF36.39",
    mid_tier_weekly: "US$7.99 ≈ CHF7.27",
    mid_tier_monthly: "US$19.99 ≈ CHF18.19",
    mid_tier_annual: "US$49.99 ≈ CHF45.49",
    top_tier_weekly: "US$9.99 ≈ CHF9.09",
    top_tier_monthly: "US$19.99 ≈ CHF18.19",
    top_tier_annual: "US$59.99 ≈ CHF54.59",
  },
  SE: {
    yearly_price_per_week: "US$0.96 ≈ SEK10.56",
    low_tier_weekly: "US$4.99 ≈ SEK54.89",
    low_tier_monthly: "US$14.99 ≈ SEK164.67",
    low_tier_annual: "US$39.99 ≈ SEK439.89",
    mid_tier_weekly: "US$7.99 ≈ SEK87.89",
    mid_tier_monthly: "US$19.99 ≈ SEK219.78",
    mid_tier_annual: "US$49.99 ≈ SEK549.89",
    top_tier_weekly: "US$9.99 ≈ SEK109.89",
    top_tier_monthly: "US$19.99 ≈ SEK219.78",
    top_tier_annual: "US$59.99 ≈ SEK659.89",
  },
  NO: {
    yearly_price_per_week: "US$0.96 ≈ NOK10.94",
    low_tier_weekly: "US$4.99 ≈ NOK56.89",
    low_tier_monthly: "US$14.99 ≈ NOK169.49",
    low_tier_annual: "US$39.99 ≈ NOK455.89",
    mid_tier_weekly: "US$7.99 ≈ NOK91.09",
    mid_tier_monthly: "US$19.99 ≈ NOK274.29",
    mid_tier_annual: "US$49.99 ≈ NOK569.89",
    top_tier_weekly: "US$9.99 ≈ NOK113.89",
    top_tier_monthly: "US$19.99 ≈ NOK274.29",
    top_tier_annual: "US$59.99 ≈ NOK683.89",
  },
  DK: {
    yearly_price_per_week: "US$0.96 ≈ DKK6.90",
    low_tier_weekly: "US$4.99 ≈ DKK35.89",
    low_tier_monthly: "US$14.99 ≈ DKK107.69",
    low_tier_annual: "US$39.99 ≈ DKK287.69",
    mid_tier_weekly: "US$7.99 ≈ DKK57.49",
    mid_tier_monthly: "US$19.99 ≈ DKK143.69",
    mid_tier_annual: "US$49.99 ≈ DKK359.69",
    top_tier_weekly: "US$9.99 ≈ DKK71.89",
    top_tier_monthly: "US$19.99 ≈ DKK143.69",
    top_tier_annual: "US$59.99 ≈ DKK431.69",
  },
  FI: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  IE: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  PT: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  GR: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.54",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  JP: {
    yearly_price_per_week: "US$0.96 ≈ ¥149",
    low_tier_weekly: "US$4.99 ≈ ¥777",
    low_tier_monthly: "US$14.99 ≈ ¥2,332",
    low_tier_annual: "US$39.99 ≈ ¥6,227",
    mid_tier_weekly: "US$7.99 ≈ ¥1,244",
    mid_tier_monthly: "US$19.99 ≈ ¥3,110",
    mid_tier_annual: "US$49.99 ≈ ¥7,784",
    top_tier_weekly: "US$9.99 ≈ ¥1,555",
    top_tier_monthly: "US$19.99 ≈ ¥3,110",
    top_tier_annual: "US$59.99 ≈ ¥9,341",
  },
  KR: {
    yearly_price_per_week: "US$0.96 ≈ ₩1,434",
    low_tier_weekly: "US$4.99 ≈ ₩7,458",
    low_tier_monthly: "US$14.99 ≈ ₩22,369",
    low_tier_annual: "US$39.99 ≈ ₩59,735",
    mid_tier_weekly: "US$7.99 ≈ ₩11,930",
    mid_tier_monthly: "US$19.99 ≈ ₩29,833",
    mid_tier_annual: "US$49.99 ≈ ₩74,600",
    top_tier_weekly: "US$9.99 ≈ ₩14,900",
    top_tier_monthly: "US$19.99 ≈ ₩29,833",
    top_tier_annual: "US$59.99 ≈ ₩89,460",
  },
  IN: {
    yearly_price_per_week: "US$0.96 ≈ ₹83",
    low_tier_weekly: "US$4.99 ≈ ₹432",
    low_tier_monthly: "US$14.99 ≈ ₹1,297",
    low_tier_annual: "US$39.99 ≈ ₹3,464",
    mid_tier_weekly: "US$7.99 ≈ ₹692",
    mid_tier_monthly: "US$19.99 ≈ ₹1,730",
    mid_tier_annual: "US$49.99 ≈ ₹4,330",
    top_tier_weekly: "US$9.99 ≈ ₹864",
    top_tier_monthly: "US$19.99 ≈ ₹1,730",
    top_tier_annual: "US$59.99 ≈ ₹5,196",
  },
  BR: {
    yearly_price_per_week: "US$0.96 ≈ R$5.81",
    low_tier_weekly: "US$4.99 ≈ R$30.20",
    low_tier_monthly: "US$14.99 ≈ R$90.80",
    low_tier_annual: "US$39.99 ≈ R$241.98",
    mid_tier_weekly: "US$7.99 ≈ R$48.36",
    mid_tier_monthly: "US$19.99 ≈ R$121.40",
    mid_tier_annual: "US$49.99 ≈ R$302.48",
    top_tier_weekly: "US$9.99 ≈ R$60.44",
    top_tier_monthly: "US$19.99 ≈ R$121.40",
    top_tier_annual: "US$59.99 ≈ R$362.98",
  },
  MX: {
    yearly_price_per_week: "US$0.96 ≈ MX$19.87",
    low_tier_weekly: "US$4.99 ≈ MX$103.29",
    low_tier_monthly: "US$14.99 ≈ MX$310.09",
    low_tier_annual: "US$39.99 ≈ MX$827.91",
    mid_tier_weekly: "US$7.99 ≈ MX$165.39",
    mid_tier_monthly: "US$19.99 ≈ MX$413.09",
    mid_tier_annual: "US$49.99 ≈ MX$1,034.91",
    top_tier_weekly: "US$9.99 ≈ MX$206.59",
    top_tier_monthly: "US$19.99 ≈ MX$413.09",
    top_tier_annual: "US$59.99 ≈ MX$1,241.91",
  },
  SG: {
    yearly_price_per_week: "US$0.96 ≈ S$1.35",
    low_tier_weekly: "US$4.99 ≈ S$7.03",
    low_tier_monthly: "US$14.99 ≈ S$21.11",
    low_tier_annual: "US$39.99 ≈ S$56.34",
    mid_tier_weekly: "US$7.99 ≈ S$11.27",
    mid_tier_monthly: "US$19.99 ≈ S$28.16",
    mid_tier_annual: "US$49.99 ≈ S$70.34",
    top_tier_weekly: "US$9.99 ≈ S$14.03",
    top_tier_monthly: "US$19.99 ≈ S$28.16",
    top_tier_annual: "US$59.99 ≈ S$84.34",
  },
  HK: {
    yearly_price_per_week: "US$0.96 ≈ HK$7.73",
    low_tier_weekly: "US$4.99 ≈ HK$40.23",
    low_tier_monthly: "US$14.99 ≈ HK$121.09",
    low_tier_annual: "US$39.99 ≈ HK$322.54",
    mid_tier_weekly: "US$7.99 ≈ HK$64.39",
    mid_tier_monthly: "US$19.99 ≈ HK$161.49",
    mid_tier_annual: "US$49.99 ≈ HK$402.54",
    top_tier_weekly: "US$9.99 ≈ HK$80.23",
    top_tier_monthly: "US$19.99 ≈ HK$161.49",
    top_tier_annual: "US$59.99 ≈ HK$482.54",
  },
  NZ: {
    yearly_price_per_week: "US$0.96 ≈ NZ$1.77",
    low_tier_weekly: "US$4.99 ≈ NZ$9.18",
    low_tier_monthly: "US$14.99 ≈ NZ$27.64",
    low_tier_annual: "US$39.99 ≈ NZ$73.62",
    mid_tier_weekly: "US$7.99 ≈ NZ$14.70",
    mid_tier_monthly: "US$19.99 ≈ NZ$36.78",
    mid_tier_annual: "US$49.99 ≈ NZ$91.98",
    top_tier_weekly: "US$9.99 ≈ NZ$18.38",
    top_tier_monthly: "US$19.99 ≈ NZ$36.78",
    top_tier_annual: "US$59.99 ≈ NZ$110.34",
  },
  ZA: {
    yearly_price_per_week: "US$0.96 ≈ R18.51",
    low_tier_weekly: "US$4.99 ≈ R96.16",
    low_tier_monthly: "US$14.99 ≈ R288.51",
    low_tier_annual: "US$39.99 ≈ R770.66",
    mid_tier_weekly: "US$7.99 ≈ R153.89",
    mid_tier_monthly: "US$19.99 ≈ R384.11",
    mid_tier_annual: "US$49.99 ≈ R962.66",
    top_tier_weekly: "US$9.99 ≈ R192.16",
    top_tier_monthly: "US$19.99 ≈ R384.11",
    top_tier_annual: "US$59.99 ≈ R1,154.66",
  },
  TH: {
    yearly_price_per_week: "US$0.96 ≈ ฿33.85",
    low_tier_weekly: "US$4.99 ≈ ฿176.00",
    low_tier_monthly: "US$14.99 ≈ ฿528.00",
    low_tier_annual: "US$39.99 ≈ ฿1,411.06",
    mid_tier_weekly: "US$7.99 ≈ ฿281.99",
    mid_tier_monthly: "US$19.99 ≈ ฿705.00",
    mid_tier_annual: "US$49.99 ≈ ฿1,763.06",
    top_tier_weekly: "US$9.99 ≈ ฿352.00",
    top_tier_monthly: "US$19.99 ≈ ฿705.00",
    top_tier_annual: "US$59.99 ≈ ฿2,115.06",
  },
  MY: {
    yearly_price_per_week: "US$0.96 ≈ RM4.45",
    low_tier_weekly: "US$4.99 ≈ RM23.14",
    low_tier_monthly: "US$14.99 ≈ RM69.42",
    low_tier_annual: "US$39.99 ≈ RM185.39",
    mid_tier_weekly: "US$7.99 ≈ RM37.06",
    mid_tier_monthly: "US$19.99 ≈ RM92.30",
    mid_tier_annual: "US$49.99 ≈ RM231.69",
    top_tier_weekly: "US$9.99 ≈ RM46.14",
    top_tier_monthly: "US$19.99 ≈ RM92.30",
    top_tier_annual: "US$59.99 ≈ RM277.99",
  },
  ID: {
    yearly_price_per_week: "US$0.96 ≈ Rp16,245",
    low_tier_weekly: "US$4.99 ≈ Rp84,445",
    low_tier_monthly: "US$14.99 ≈ Rp254,000",
    low_tier_annual: "US$39.99 ≈ Rp676,828",
    mid_tier_weekly: "US$7.99 ≈ Rp135,260",
    mid_tier_monthly: "US$19.99 ≈ Rp339,000",
    mid_tier_annual: "US$49.99 ≈ Rp846,028",
    top_tier_weekly: "US$9.99 ≈ Rp168,890",
    top_tier_monthly: "US$19.99 ≈ Rp339,000",
    top_tier_annual: "US$59.99 ≈ Rp1,015,228",
  },
  PH: {
    yearly_price_per_week: "US$0.96 ≈ ₱58.17",
    low_tier_weekly: "US$4.99 ≈ ₱302.50",
    low_tier_monthly: "US$14.99 ≈ ₱907.50",
    low_tier_annual: "US$39.99 ≈ ₱2,424.80",
    mid_tier_weekly: "US$7.99 ≈ ₱484.00",
    mid_tier_monthly: "US$19.99 ≈ ₱1,210.00",
    mid_tier_annual: "US$49.99 ≈ ₱3,024.80",
    top_tier_weekly: "US$9.99 ≈ ₱605.00",
    top_tier_monthly: "US$19.99 ≈ ₱1,210.00",
    top_tier_annual: "US$59.99 ≈ ₱3,624.80",
  },
  VN: {
    yearly_price_per_week: "US$0.96 ≈ ₫24,217",
    low_tier_weekly: "US$4.99 ≈ ₫125,751",
    low_tier_monthly: "US$14.99 ≈ ₫377,253",
    low_tier_annual: "US$39.99 ≈ ₫1,007,900",
    mid_tier_weekly: "US$7.99 ≈ ₫201,429",
    mid_tier_monthly: "US$19.99 ≈ ₫503,205",
    mid_tier_annual: "US$49.99 ≈ ₫1,259,900",
    top_tier_weekly: "US$9.99 ≈ ₫250,751",
    top_tier_monthly: "US$19.99 ≈ ₫503,205",
    top_tier_annual: "US$59.99 ≈ ₫1,511,900",
  },
  TR: {
    yearly_price_per_week: "US$0.96 ≈ ₺35.44",
    low_tier_weekly: "US$4.99 ≈ ₺184.23",
    low_tier_monthly: "US$14.99 ≈ ₺553.13",
    low_tier_annual: "US$39.99 ≈ ₺1,476.24",
    mid_tier_weekly: "US$7.99 ≈ ₺295.00",
    mid_tier_monthly: "US$19.99 ≈ ₺737.50",
    mid_tier_annual: "US$49.99 ≈ ₺1,845.24",
    top_tier_weekly: "US$9.99 ≈ ₺368.23",
    top_tier_monthly: "US$19.99 ≈ ₺737.50",
    top_tier_annual: "US$59.99 ≈ ₺2,214.24",
  },
  PL: {
    yearly_price_per_week: "US$0.96 ≈ zł4.08",
    low_tier_weekly: "US$4.99 ≈ zł21.23",
    low_tier_monthly: "US$14.99 ≈ zł63.69",
    low_tier_annual: "US$39.99 ≈ zł170.04",
    mid_tier_weekly: "US$7.99 ≈ zł34.00",
    mid_tier_monthly: "US$19.99 ≈ zł84.99",
    mid_tier_annual: "US$49.99 ≈ zł212.54",
    top_tier_weekly: "US$9.99 ≈ zł42.23",
    top_tier_monthly: "US$19.99 ≈ zł84.99",
    top_tier_annual: "US$59.99 ≈ zł255.04",
  },
  CZ: {
    yearly_price_per_week: "US$0.96 ≈ Kč24.17",
    low_tier_weekly: "US$4.99 ≈ Kč125.64",
    low_tier_monthly: "US$14.99 ≈ Kč376.92",
    low_tier_annual: "US$39.99 ≈ Kč1,006.93",
    mid_tier_weekly: "US$7.99 ≈ Kč201.29",
    mid_tier_monthly: "US$19.99 ≈ Kč499.99",
    mid_tier_annual: "US$49.99 ≈ Kč1,258.43",
    top_tier_weekly: "US$9.99 ≈ Kč250.64",
    top_tier_monthly: "US$19.99 ≈ Kč499.99",
    top_tier_annual: "US$59.99 ≈ Kč1,509.93",
  },
  HU: {
    yearly_price_per_week: "US$0.96 ≈ Ft395",
    low_tier_weekly: "US$4.99 ≈ Ft2,053",
    low_tier_monthly: "US$14.99 ≈ Ft6,159",
    low_tier_annual: "US$39.99 ≈ Ft16,451",
    mid_tier_weekly: "US$7.99 ≈ Ft3,287",
    mid_tier_monthly: "US$19.99 ≈ Ft8,195",
    mid_tier_annual: "US$49.99 ≈ Ft20,551",
    top_tier_weekly: "US$9.99 ≈ Ft4,053",
    top_tier_monthly: "US$19.99 ≈ Ft8,195",
    top_tier_annual: "US$59.99 ≈ Ft24,651",
  },
  RO: {
    yearly_price_per_week: "US$0.96 ≈ lei4.78",
    low_tier_weekly: "US$4.99 ≈ lei24.83",
    low_tier_monthly: "US$14.99 ≈ lei74.33",
    low_tier_annual: "US$39.99 ≈ lei199.03",
    mid_tier_weekly: "US$7.99 ≈ lei39.75",
    mid_tier_monthly: "US$19.99 ≈ lei99.25",
    mid_tier_annual: "US$49.99 ≈ lei248.53",
    top_tier_weekly: "US$9.99 ≈ lei49.83",
    top_tier_monthly: "US$19.99 ≈ lei99.25",
    top_tier_annual: "US$59.99 ≈ lei298.03",
  },
  BG: {
    yearly_price_per_week: "US$0.96 ≈ лв1.88",
    low_tier_weekly: "US$4.99 ≈ лв9.76",
    low_tier_monthly: "US$14.99 ≈ лв29.29",
    low_tier_annual: "US$39.99 ≈ лв78.23",
    mid_tier_weekly: "US$7.99 ≈ лв15.60",
    mid_tier_monthly: "US$19.99 ≈ лв39.13",
    mid_tier_annual: "US$49.99 ≈ лв97.73",
    top_tier_weekly: "US$9.99 ≈ лв19.76",
    top_tier_monthly: "US$19.99 ≈ лв39.13",
    top_tier_annual: "US$59.99 ≈ лв117.23",
  },
  HR: {
    yearly_price_per_week: "US$0.96 ≈ €0.93",
    low_tier_weekly: "US$4.99 ≈ €4.84",
    low_tier_monthly: "US$14.99 ≈ €14.52",
    low_tier_annual: "US$39.99 ≈ €38.79",
    mid_tier_weekly: "US$7.99 ≈ €7.75",
    mid_tier_monthly: "US$19.99 ≈ €19.49",
    mid_tier_annual: "US$49.99 ≈ €48.49",
    top_tier_weekly: "US$9.99 ≈ €9.69",
    top_tier_monthly: "US$19.99 ≈ €19.49",
    top_tier_annual: "US$59.99 ≈ €58.19",
  },
  IL: {
    yearly_price_per_week: "US$0.96 ≈ ₪3.55",
    low_tier_weekly: "US$4.99 ≈ ₪18.47",
    low_tier_monthly: "US$14.99 ≈ ₪55.63",
    low_tier_annual: "US$39.99 ≈ ₪148.06",
    mid_tier_weekly: "US$7.99 ≈ ₪29.55",
    mid_tier_monthly: "US$19.99 ≈ ₪74.18",
    mid_tier_annual: "US$49.99 ≈ ₪184.96",
    top_tier_weekly: "US$9.99 ≈ ₪36.94",
    top_tier_monthly: "US$19.99 ≈ ₪74.18",
    top_tier_annual: "US$59.99 ≈ ₪221.86",
  },
  AE: {
    yearly_price_per_week: "US$0.96 ≈ AED3.53",
    low_tier_weekly: "US$4.99 ≈ AED18.33",
    low_tier_monthly: "US$14.99 ≈ AED54.97",
    low_tier_annual: "US$39.99 ≈ AED146.94",
    mid_tier_weekly: "US$7.99 ≈ AED29.33",
    mid_tier_monthly: "US$19.99 ≈ AED73.34",
    mid_tier_annual: "US$49.99 ≈ AED183.54",
    top_tier_weekly: "US$9.99 ≈ AED36.66",
    top_tier_monthly: "US$19.99 ≈ AED73.34",
    top_tier_annual: "US$59.99 ≈ AED220.14",
  },
  SA: {
    yearly_price_per_week: "US$0.96 ≈ SAR3.60",
    low_tier_weekly: "US$4.99 ≈ SAR18.71",
    low_tier_monthly: "US$14.99 ≈ SAR56.13",
    low_tier_annual: "US$39.99 ≈ SAR149.98",
    mid_tier_weekly: "US$7.99 ≈ SAR29.93",
    mid_tier_monthly: "US$19.99 ≈ SAR74.67",
    mid_tier_annual: "US$49.99 ≈ SAR187.48",
    top_tier_weekly: "US$9.99 ≈ SAR37.41",
    top_tier_monthly: "US$19.99 ≈ SAR74.67",
    top_tier_annual: "US$59.99 ≈ SAR224.98",
  },
  CO: {
    yearly_price_per_week: "US$0.96 ≈ 3,858 COP",
    low_tier_weekly: "US$4.99 ≈ 20,061 COP",
    low_tier_monthly: "US$14.99 ≈ 60,183 COP",
    low_tier_annual: "US$39.99 ≈ 160,600 COP",
    mid_tier_weekly: "US$7.99 ≈ 32,150 COP",
    mid_tier_monthly: "US$19.99 ≈ 80,400 COP",
    mid_tier_annual: "US$49.99 ≈ 200,600 COP",
    top_tier_weekly: "US$9.99 ≈ 40,061 COP",
    top_tier_monthly: "US$19.99 ≈ 80,400 COP",
    top_tier_annual: "US$59.99 ≈ 240,600 COP",
  },
};

AlertWidgetPaywall.getWebPackages = (
  country = useAppStore.getState().country,
  webSubscriptionPlans = useAppStore.getState()
    .webSubscriptionPlans as WebSubscriptionPlans,
): PurchasePackage[] => {
  const normalizedCountry = country || "US";
  const countryPrices = (
    AlertWidgetPaywall.priceMapper as Record<
      string,
      Partial<Record<SubscriptionPlan | "yearly_price_per_week", string>>
    >
  )[normalizedCountry];
  const configuredPlans = Object.values(webSubscriptionPlans).filter(
    Boolean,
  ) as SubscriptionPlan[];
  const selectedPlans =
    configuredPlans.length > 0
      ? configuredPlans.slice(0, 2)
      : (["low_tier_monthly", "low_tier_annual"] as SubscriptionPlan[]);
  const yearlyPricePerWeekString =
    countryPrices?.yearly_price_per_week || "US$0.96";

  // Extract numeric price from price string (handles different currencies)
  const extractPrice = (priceString: string): number => {
    const match = priceString.match(/[\d,]+\.?\d*/);
    if (!match) return 0;
    return parseFloat(match[0].replace(/,/g, ""));
  };

  const yearlyPricePerWeek = extractPrice(yearlyPricePerWeekString);

  return selectedPlans.map((plan) => {
    const priceString = countryPrices?.[plan] || getDefaultPriceString(plan);
    const price = extractPrice(priceString);
    const isAnnual = getBillingPeriodFromIdentifier(plan) === "annual";

    return {
      identifier: plan,
      product: {
        price,
        priceString,
        pricePerWeekString: isAnnual ? yearlyPricePerWeekString : priceString,
        pricePerWeek: isAnnual ? yearlyPricePerWeek : price,
      },
    };
  });
};

export default AlertWidgetPaywall;
